import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  apiCreateVault,
  apiDeleteVault,
  apiFetchVaults,
  apiRenameVault,
  apiVaultPinChange,
  apiVaultPinSet,
  apiVaultPinStatus,
  apiVaultPinVerify,
  type VaultWire,
} from "../../lib/authApi";
import { loadVaultAutolockMinutes, saveVaultAutolockMinutes } from "../../lib/db";
import { useSessionResetRegistry } from "../SessionResetContext";
import { useAuth } from "./AuthContext";
import { useRealtime } from "./RealtimeContext";

const PIN_RE = /^[0-9]{5}$/;

export type VaultContextValue = {
  hasPin: boolean;
  unlocked: boolean;
  vaults: VaultWire[];
  loading: boolean;
  pinError: string | null;
  autoLockMinutes: number | null;
  refreshPinStatus: () => Promise<void>;
  refreshVaults: () => Promise<void>;
  setInitialPinAndFirstVault: (pin: string) => Promise<void>;
  verifyPinAndUnlock: (pin: string) => Promise<boolean>;
  lock: () => void;
  touchActivity: () => void;
  createVault: (name: string) => Promise<void>;
  renameVault: (vaultId: string, name: string) => Promise<void>;
  deleteVault: (vaultId: string) => Promise<void>;
  changePin: (oldPin: string, newPin: string) => Promise<void>;
  setAutoLockMinutes: (minutes: number | null) => Promise<void>;
};

const VaultContext = createContext<VaultContextValue | null>(null);

export function VaultProvider({ children }: { children: ReactNode }) {
  const { register } = useSessionResetRegistry();
  const { identityRow } = useAuth();
  const { authenticated: rtAuth } = useRealtime();

  const [hasPin, setHasPin] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [vaults, setVaults] = useState<VaultWire[]>([]);
  const [loading, setLoading] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [autoLockMinutes, setAutoLockMinutesState] = useState<number | null>(5);
  const autoLockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAutoLockTimer = useCallback(() => {
    if (autoLockTimerRef.current != null) {
      clearTimeout(autoLockTimerRef.current);
      autoLockTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return register(() => {
      clearAutoLockTimer();
      setHasPin(false);
      setUnlocked(false);
      setVaults([]);
      setPinError(null);
    });
  }, [register, clearAutoLockTimer]);

  const refreshPinStatus = useCallback(async () => {
    if (!rtAuth) {
      setHasPin(false);
      setUnlocked(false);
      return;
    }
    try {
      const s = await apiVaultPinStatus();
      setHasPin(s.has_pin);
      if (!s.has_pin) {
        setUnlocked(false);
        setVaults([]);
      }
    } catch {
      setHasPin(false);
    }
  }, [rtAuth]);

  const refreshVaults = useCallback(async () => {
    if (!rtAuth || !unlocked) return;
    try {
      const list = await apiFetchVaults();
      setVaults(list);
    } catch {
      /* keep list */
    }
  }, [rtAuth, unlocked]);

  useEffect(() => {
    void refreshPinStatus();
  }, [refreshPinStatus]);

  useEffect(() => {
    if (!identityRow?.userId) return;
    void loadVaultAutolockMinutes().then(setAutoLockMinutesState);
  }, [identityRow?.userId]);

  useEffect(() => {
    if (unlocked) void refreshVaults();
  }, [unlocked, refreshVaults]);

  const scheduleAutoLock = useCallback(() => {
    clearAutoLockTimer();
    if (!unlocked || autoLockMinutes == null) return;
    const ms = autoLockMinutes * 60_000;
    autoLockTimerRef.current = setTimeout(() => {
      setUnlocked(false);
      setVaults([]);
    }, ms);
  }, [unlocked, autoLockMinutes, clearAutoLockTimer]);

  useEffect(() => {
    if (unlocked) scheduleAutoLock();
    else clearAutoLockTimer();
    return () => clearAutoLockTimer();
  }, [unlocked, scheduleAutoLock, clearAutoLockTimer]);

  const touchActivity = useCallback(() => {
    if (unlocked && autoLockMinutes != null) scheduleAutoLock();
  }, [unlocked, autoLockMinutes, scheduleAutoLock]);

  const lock = useCallback(() => {
    clearAutoLockTimer();
    setUnlocked(false);
    setVaults([]);
  }, [clearAutoLockTimer]);

  const setInitialPinAndFirstVault = useCallback(
    async (pin: string) => {
      setPinError(null);
      if (!PIN_RE.test(pin)) {
        setPinError("Пин-код — ровно 5 цифр");
        return;
      }
      setLoading(true);
      try {
        await apiVaultPinSet(pin);
        setHasPin(true);
        setUnlocked(true);
        await apiCreateVault("Хранилище");
        await refreshVaults();
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setPinError(msg);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [refreshVaults],
  );

  const verifyPinAndUnlock = useCallback(async (pin: string): Promise<boolean> => {
    setPinError(null);
    if (!PIN_RE.test(pin)) {
      setPinError("Пин-код — ровно 5 цифр");
      return false;
    }
    setLoading(true);
    try {
      const ok = await apiVaultPinVerify(pin);
      if (!ok) {
        setPinError("Неверный пин-код");
        return false;
      }
      setUnlocked(true);
      await refreshVaults();
      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setPinError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, [refreshVaults]);

  const createVault = useCallback(
    async (name: string) => {
      const t = name.trim();
      if (t.length < 1) return;
      await apiCreateVault(t);
      await refreshVaults();
    },
    [refreshVaults],
  );

  const renameVault = useCallback(
    async (vaultId: string, name: string) => {
      await apiRenameVault(vaultId, name.trim());
      await refreshVaults();
    },
    [refreshVaults],
  );

  const deleteVault = useCallback(
    async (vaultId: string) => {
      await apiDeleteVault(vaultId);
      await refreshVaults();
    },
    [refreshVaults],
  );

  const changePin = useCallback(async (oldPin: string, newPin: string) => {
    if (!PIN_RE.test(oldPin) || !PIN_RE.test(newPin)) {
      throw new Error("INVALID_PIN_FORMAT");
    }
    await apiVaultPinChange(oldPin, newPin);
  }, []);

  const setAutoLockMinutes = useCallback(async (minutes: number | null) => {
    await saveVaultAutolockMinutes(minutes);
    setAutoLockMinutesState(minutes);
    if (minutes == null) clearAutoLockTimer();
    else if (unlocked) scheduleAutoLock();
  }, [unlocked, clearAutoLockTimer, scheduleAutoLock]);

  const value = useMemo<VaultContextValue>(
    () => ({
      hasPin,
      unlocked,
      vaults,
      loading,
      pinError,
      autoLockMinutes,
      refreshPinStatus,
      refreshVaults,
      setInitialPinAndFirstVault,
      verifyPinAndUnlock,
      lock,
      touchActivity,
      createVault,
      renameVault,
      deleteVault,
      changePin,
      setAutoLockMinutes,
    }),
    [
      hasPin,
      unlocked,
      vaults,
      loading,
      pinError,
      autoLockMinutes,
      refreshPinStatus,
      refreshVaults,
      setInitialPinAndFirstVault,
      verifyPinAndUnlock,
      lock,
      touchActivity,
      createVault,
      renameVault,
      deleteVault,
      changePin,
      setAutoLockMinutes,
    ],
  );

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
}

export function useVault(): VaultContextValue {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error("useVault outside VaultProvider");
  return ctx;
}
